import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle } from "lucide-react";
import { FilterBar } from "@/components/ui/FilterBar";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useModules } from "@/context/ModulesContext";
import { formatCurrency } from "@/utils/format";
import { cn } from "@/utils/cn";
import {
  fetchInadimplencia,
  type InadimplenciaClienteRow,
  type InadimplenciaPayload,
} from "@/services/inadimplencia";
import {
  CORTE_LABEL,
  INADIMPLENTE_DIAS_MIN,
  REGUA_LABEL,
  SPC_LABEL,
  agingFaixa,
  defaultInadimplenciaPeriod,
  dueDateInCorte,
  formatAnoMes,
  type AgingFaixa,
  type CortePeriodo,
} from "../inadimplenciaUtils";
import { formatDate } from "@/utils/format";
import { ClienteDrawer } from "./ClienteDrawer";
import {
  AgingBarChart,
  CortesValorChart,
  EmpreendimentoTaxaChart,
  EmpreendimentoVolumeChart,
  EvolucaoMensalChart,
  KpiDonutCard,
} from "./InadimplenciaCharts";
import { InadimplenciaDetailModal, type InadimplenciaDetalhe } from "./InadimplenciaDetailModal";
import type { InadimplenciaParcela } from "@/services/inadimplencia";

interface Props {
  refreshToken?: number;
}

export function InadimplenciaPanel({ refreshToken = 0 }: Props) {
  const { financeiro, ccOptions, loadingCC, ccNameMap } = useModules();
  const [draft, setDraft] = useState(() => {
    const period = defaultInadimplenciaPeriod();
    return {
      startDate: period.startDate,
      endDate: period.endDate,
      costCenter: financeiro.values.costCenter ?? [],
    };
  });
  const [applied, setApplied] = useState(draft);
  const [data, setData] = useState<InadimplenciaPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [faixa, setFaixa] = useState<AgingFaixa | "todas" | "spc30">("todas");
  const [lojaKey, setLojaKey] = useState("");
  const [selected, setSelected] = useState<InadimplenciaClienteRow | null>(null);
  const [detalhe, setDetalhe] = useState<InadimplenciaDetalhe | null>(null);

  const load = useCallback(async (refresh = false) => {
    if (!applied.endDate) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchInadimplencia({
        startDate: applied.startDate,
        cutDate: applied.endDate,
        costCenterIds: applied.costCenter,
        refresh,
      });
      setData(payload);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar inadimplência.");
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => {
    void load(refreshToken > 0);
  }, [load, refreshToken]);

  const clientesFiltrados = useMemo(() => {
    const list = data?.clientes ?? [];
    const query = q.trim().toLowerCase();
    return list.filter((c) => {
      if (!c.ativo) return false;
      if (faixa === "spc30" && !c.prioridadeSpc) return false;
      if (faixa !== "todas" && faixa !== "spc30" && c.faixaAtraso !== faixa) return false;
      if (lojaKey) {
        const loja = data?.porLoja.find((x) => x.key === lojaKey);
        const clientKeyLoja = c.costCenterId || c.costCenterName || "nao-informado";
        if (c.costCenterId !== lojaKey && clientKeyLoja !== lojaKey && (c.costCenterName || "Não informado") !== loja?.label) {
          return false;
        }
      }
      if (query && !c.clientName.toLowerCase().includes(query) && !(c.clientDocument ?? "").includes(query)) {
        return false;
      }
      return true;
    });
  }, [data, q, faixa, lojaKey]);

  const isFirst = loading && !data;
  const kpis = data?.kpis;
  const porEmpreendimento = useMemo(
    () =>
      (data?.porLoja ?? []).map((row) => ({
        ...row,
        label: ccNameMap.get(row.key) || row.label,
      })),
    [data, ccNameMap],
  );

  function clientesDosBoletos(boletos: InadimplenciaParcela[]): InadimplenciaClienteRow[] {
    const existing = new Map((data?.clientes ?? []).map((c) => [c.clientKey, c]));
    const keys = [...new Set(boletos.map((b) => b.clientKey))];
    return keys.map((key) => {
      const found = existing.get(key);
      if (found) return found;
      const ofClient = boletos.filter((b) => b.clientKey === key);
      const first = ofClient[0];
      const vencidos = ofClient.filter((b) => !b.isPaid && !b.emAcordo && b.daysOverdue > INADIMPLENTE_DIAS_MIN);
      return {
        clientKey: key,
        clientId: first?.clientId,
        clientName: first?.clientName ?? "Cliente não informado",
        clientDocument: first?.clientDocument,
        ativo: true,
        inadimplente: vencidos.length > 0,
        emAcordo: ofClient.some((b) => b.emAcordo),
        spcStatus: "nao_consultado" as const,
        maxDaysOverdue: Math.max(0, ...ofClient.map((b) => b.daysOverdue)),
        valorVencido: vencidos.reduce((s, b) => s + b.balanceAmount, 0),
        valorCarteira: ofClient.filter((b) => !b.isPaid).reduce((s, b) => s + b.balanceAmount, 0),
        faixaAtraso: null,
        regua: "lembrete" as const,
        prioridadeSpc: false,
      };
    });
  }

  function openDetalhe(partial: InadimplenciaDetalhe) {
    if (!data) return;
    setDetalhe(partial);
  }

  function boletosDoPeriodo(): InadimplenciaParcela[] {
    const start = applied.startDate;
    const end = data?.cutDate ?? applied.endDate;
    const fonte = data?.boletosCarteira?.length ? data.boletosCarteira : (data?.boletos ?? []);
    return fonte.filter((b) => {
      const d = (b.dueDate ?? "").slice(0, 10);
      if (start && d < start) return false;
      if (end && d > end) return false;
      return true;
    });
  }

  function openCarteira() {
    const boletos = boletosDoPeriodo();
    openDetalhe({
      title: "Vencimentos do período",
      color: "#2dd4bf",
      boletos,
      clientes: clientesDosBoletos(boletos),
      totalLabel: "Valor com vencimento",
      totalValue: kpis?.carteira ?? 0,
    });
  }

  function openVencido() {
    const boletos = (data?.boletos ?? []).filter((b) => !b.emAcordo && b.daysOverdue > INADIMPLENTE_DIAS_MIN);
    openDetalhe({
      title: "Vencido — parcelas inadimplentes",
      color: "#f43f5e",
      boletos,
      clientes: (data?.clientes ?? []).filter((c) => c.inadimplente),
      totalLabel: "Vencido",
      totalValue: kpis?.vencido ?? 0,
    });
  }

  function openAcordo() {
    const boletos = (data?.boletos ?? []).filter((b) => b.emAcordo);
    openDetalhe({
      title: "Em acordo — parcelas negociadas",
      color: "#38bdf8",
      boletos,
      clientes: (data?.clientes ?? []).filter((c) => c.emAcordo),
      totalLabel: "Em acordo",
      totalValue: kpis?.emAcordo ?? 0,
    });
  }

  function openInadimplentes() {
    const clientes = (data?.clientes ?? []).filter((c) => c.inadimplente);
    const keys = new Set(clientes.map((c) => c.clientKey));
    const boletos = (data?.boletos ?? []).filter((b) => keys.has(b.clientKey) && !b.emAcordo && b.daysOverdue > INADIMPLENTE_DIAS_MIN);
    openDetalhe({
      title: "Clientes inadimplentes",
      color: "#8b5cf6",
      boletos,
      clientes,
      totalLabel: "Vencido dos inadimplentes",
      totalValue: kpis?.vencido ?? 0,
    });
  }

  function openAging(faixaSel: AgingFaixa) {
    const boletos = (data?.boletos ?? []).filter(
      (b) => !b.emAcordo && agingFaixa(b.daysOverdue) === faixaSel,
    );
    openDetalhe({
      title: `Aging do atraso — ${faixaSel} dias`,
      color: "#fb7185",
      boletos,
      clientes: clientesDosBoletos(boletos),
      totalLabel: "Saldo na faixa",
      totalValue: data?.aging[faixaSel] ?? 0,
    });
  }

  function openCorte(corte: CortePeriodo, serie: "carteira" | "vencido") {
    const cut = data?.cutDate ?? applied.endDate;
    const fonte = data?.boletosCarteira?.length ? data.boletosCarteira : (data?.boletos ?? []);
    const doCorte = fonte.filter((b) => dueDateInCorte(b.dueDate, cut, corte));
    const boletos = serie === "vencido"
      ? doCorte.filter((b) => !b.isPaid && !b.emAcordo && b.daysOverdue > INADIMPLENTE_DIAS_MIN)
      : doCorte;
    const row = data?.cortes[corte];
    const qtd = serie === "vencido" ? (row?.qtdVencido ?? boletos.length) : (row?.qtdTotal ?? boletos.length);
    openDetalhe({
      title: `Inadimplência por vencimento — ${CORTE_LABEL[corte]}`,
      color: serie === "vencido" ? "#f43f5e" : "#2dd4bf",
      boletos,
      clientes: clientesDosBoletos(boletos),
      totalLabel: serie === "vencido"
        ? `${qtd} parcela(s) inadimplente(s)`
        : `${qtd} parcela(s) com vencimento`,
      totalValue: (serie === "vencido" ? row?.vencido : row?.carteira)
        ?? boletos.reduce((s, b) => s + (serie === "vencido" ? b.balanceAmount : b.originalAmount), 0),
    });
  }

  function openEmpreendimento(key: string, label: string) {
    const fonte = boletosDoPeriodo();
    const boletos = fonte.filter((b) => {
      const k = b.costCenterId || (b.costCenterName ? `nome:${b.costCenterName}` : "nao-informado");
      return k === key || (b.costCenterName || "Não informado") === label;
    });
    const row = porEmpreendimento.find((r) => r.key === key);
    openDetalhe({
      title: `Empreendimento — ${label}`,
      color: "#fb7185",
      boletos,
      clientes: clientesDosBoletos(boletos),
      totalLabel: `${row?.qtdVencido ?? 0} de ${row?.qtdTotal ?? boletos.length} parcelas`,
      totalValue: row?.carteira ?? boletos.reduce((s, b) => s + b.originalAmount, 0),
    });
  }

  function openMes(anoMes: string) {
    const fonte = data?.boletosCarteira?.length ? data.boletosCarteira : boletosDoPeriodo();
    const boletos = fonte.filter((b) => (b.dueDate ?? "").slice(0, 7) === anoMes);
    const row = data?.serieMensal?.find((s) => s.anoMes === anoMes);
    openDetalhe({
      title: `Evolução mensal — ${formatAnoMes(anoMes)}`,
      color: "#fb7185",
      boletos,
      clientes: clientesDosBoletos(boletos),
      totalLabel: `${row?.qtdVencido ?? 0} de ${row?.qtdTotal ?? boletos.length} parcelas`,
      totalValue: row?.carteira ?? boletos.reduce((s, b) => s + b.originalAmount, 0),
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <FilterBar
        values={{
          ...financeiro.values,
          startDate: draft.startDate,
          endDate: draft.endDate,
          costCenter: draft.costCenter,
        }}
        onChange={(v) =>
          setDraft({
            startDate: v.startDate,
            endDate: v.endDate,
            costCenter: v.costCenter,
          })
        }
        onApply={() => setApplied(draft)}
        onReset={() => {
          const period = defaultInadimplenciaPeriod();
          const next = {
            startDate: period.startDate,
            endDate: period.endDate,
            costCenter: [],
          };
          setDraft(next);
          setApplied(next);
        }}
        costCenterOptions={ccOptions}
        loadingOptions={loadingCC}
        loading={loading}
      />

      <p className="text-xs text-slate-500">
        Vencimentos de {formatDate(applied.startDate)} a {formatDate(applied.endDate)}.
        Atraso calculado até {formatDate(applied.endDate)}.
        Só parcelas com condição PP ou PM/PMM. Inadimplente: vencido há mais de 5 dias, fora de acordo.
        Vencido usa saldo + acréscimo do extrato Sienge. Reparcelamentos de qualquer cliente não entram no cálculo.
      </p>

      {error && (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
          <button type="button" onClick={() => void load(true)} className="ml-auto text-xs underline">
            Tentar novamente
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        <KpiDonutCard
          title="% clientes"
          value={kpis?.taxaClientes ?? 0}
          subtitle={isFirst ? undefined : `${kpis?.clientesInadimplentes ?? 0} de ${kpis?.clientesAtivos ?? 0} ativos`}
          color="#fb7185"
          loading={isFirst}
          accent="border-l-rose-400"
          onClick={data ? openInadimplentes : undefined}
        />
        <KpiDonutCard
          title="% valor"
          value={kpis?.taxaValor ?? 0}
          subtitle={
            isFirst
              ? undefined
              : `${kpis?.qtdTitulosVencidos ?? 0} de ${kpis?.qtdTitulos ?? 0} parcelas com vencimento`
          }
          color="#fb923c"
          loading={isFirst}
          accent="border-l-orange-400"
          onClick={data ? openVencido : undefined}
        />
        <Card
          title="Vencimentos"
          value={isFirst ? undefined : formatCurrency(kpis?.carteira ?? 0)}
          subtitle={isFirst ? undefined : "Valor com vencimento no período"}
          loading={isFirst}
          className="border-l-4 border-l-teal-400"
          onClick={data ? openCarteira : undefined}
        />
        <Card
          title="Vencido"
          value={isFirst ? undefined : formatCurrency(kpis?.vencido ?? 0)}
          loading={isFirst}
          className="border-l-4 border-l-red-400"
          onClick={data ? openVencido : undefined}
        />
        <Card
          title="Em acordo"
          value={isFirst ? undefined : formatCurrency(kpis?.emAcordo ?? 0)}
          loading={isFirst}
          className="border-l-4 border-l-sky-400"
          onClick={data ? openAcordo : undefined}
        />
        <Card
          title="Inadimplentes"
          value={isFirst ? undefined : String(kpis?.clientesInadimplentes ?? 0)}
          loading={isFirst}
          className="border-l-4 border-l-violet-400"
          onClick={data ? openInadimplentes : undefined}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <CortesValorChart
          cortes={data?.cortes}
          loading={isFirst}
          ultimoFechamento={data?.ultimoFechamento}
          onSelectCorte={data ? openCorte : undefined}
        />
        <AgingBarChart
          aging={data?.aging}
          loading={isFirst}
          selected={faixa}
          onSelect={(a) => {
            setFaixa((cur) => (cur === a ? "todas" : a));
            if (data) openAging(a);
          }}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <EmpreendimentoTaxaChart
          rows={porEmpreendimento}
          loading={isFirst}
          selectedKey={lojaKey}
          onSelect={(key, label) => {
            setLojaKey((cur) => (cur === key ? "" : key));
            if (data) openEmpreendimento(key, label);
          }}
        />
        <EmpreendimentoVolumeChart
          rows={porEmpreendimento}
          loading={isFirst}
          selectedKey={lojaKey}
          onSelect={(key, label) => {
            setLojaKey((cur) => (cur === key ? "" : key));
            if (data) openEmpreendimento(key, label);
          }}
        />
      </div>

      <EvolucaoMensalChart
        serie={data?.serieMensal ?? []}
        loading={isFirst}
        onSelectMes={data ? openMes : undefined}
      />

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-800">Clientes</h2>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar cliente ou documento"
            className="h-9 min-w-[200px] flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm"
          />
          <Button
            size="sm"
            variant={faixa === "spc30" ? "primary" : "secondary"}
            onClick={() => setFaixa((cur) => (cur === "spc30" ? "todas" : "spc30"))}
          >
            Prioridade SPC (&gt; 30 dias)
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setFaixa("todas"); setLojaKey(""); setQ(""); }}>
            Limpar filtros
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-xs">
            <thead className="bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2.5">Cliente</th>
                <th className="px-3 py-2.5 text-right">Atraso</th>
                <th className="px-3 py-2.5 text-right">Vencido</th>
                <th className="px-3 py-2.5 text-right">Carteira</th>
                <th className="px-3 py-2.5">Régua</th>
                <th className="px-3 py-2.5">SPC</th>
                <th className="px-3 py-2.5">Acordo</th>
              </tr>
            </thead>
            <tbody>
              {isFirst && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                    Consultando o Sienge… a primeira carga da carteira pode levar alguns minutos.
                  </td>
                </tr>
              )}
              {!isFirst && clientesFiltrados.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                    Nenhum cliente no filtro.
                  </td>
                </tr>
              )}
              {clientesFiltrados.map((c) => (
                <tr
                  key={c.clientKey}
                  className={cn(
                    "cursor-pointer border-t border-slate-100 hover:bg-slate-50",
                    c.prioridadeSpc && "bg-rose-50/40",
                  )}
                  onClick={() => setSelected(c)}
                >
                  <td className="px-3 py-2.5 font-medium text-slate-800">{c.clientName}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {c.maxDaysOverdue > 0 ? `${c.maxDaysOverdue} d` : "—"}
                    {c.faixaAtraso ? <span className="ml-1 text-slate-400">{c.faixaAtraso}</span> : null}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{formatCurrency(c.valorVencido)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{formatCurrency(c.valorCarteira)}</td>
                  <td className="px-3 py-2.5 text-slate-600">{REGUA_LABEL[c.regua]}</td>
                  <td className="px-3 py-2.5">{SPC_LABEL[c.spcStatus]}</td>
                  <td className="px-3 py-2.5">{c.emAcordo ? "Em acordo" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <InadimplenciaDetailModal
        open={!!detalhe}
        detalhe={detalhe}
        onClose={() => setDetalhe(null)}
        onSelectCliente={(c) => setSelected(c)}
      />

      <ClienteDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        cliente={selected}
        boletos={data?.boletos ?? []}
        onUpdated={() => void load(false)}
      />
    </div>
  );
}

